import type { AdResult } from '../mocks/adResults';
import { getSupabaseFunctionUrl, supabase } from '@/lib/supabase';

const API_URL = getSupabaseFunctionUrl('generate-ads');

export interface AdFormData {
  businessName: string;
  businessType: string;
  businessDescription: string;
  audience: string;
  offer: string;
  platform: string;
  tone: string;
  // Extended fields for profile-based generation
  businessProfileId?: string;
  campaignGoal?: string;
  campaignNotes?: string;
  servicesOffered?: string;
  location?: string;
  brandPositioning?: string;
  adAngle?: string;
  uniqueDifferentiator?: string;
  creativeDirection?: string;
  stylePack?: string;
  stylePackIds?: string[];
  imageCount?: number;
  referenceImageBase64?: string;
  // Image engine / quality mode
  imageEngine?: string;
  provider?: string;
}

export class QuotaExceededError extends Error {
  constructor() {
    super('QUOTA_EXCEEDED');
    this.name = 'QuotaExceededError';
  }
}

export class InsufficientCreditsError extends Error {
  creditsRemaining: number;
  constructor(creditsRemaining = 0) {
    super('INSUFFICIENT_CREDITS');
    this.name = 'InsufficientCreditsError';
    this.creditsRemaining = creditsRemaining;
  }
}

export class ProBulkCapError extends Error {
  dailyCap: number;
  used: number;
  constructor(dailyCap = 3, used = 3) {
    super('PRO_BULK_DAILY_CAP_EXCEEDED');
    this.name = 'ProBulkCapError';
    this.dailyCap = dailyCap;
    this.used = used;
  }
}

export class ProBulkCooldownError extends Error {
  remainingMin: number;
  constructor(remainingMin = 10) {
    super('PRO_BULK_COOLDOWN');
    this.name = 'ProBulkCooldownError';
    this.remainingMin = remainingMin;
  }
}

interface GenerateAdsOptions {
  signal?: AbortSignal;
}

function isGenerationAborted(signal?: AbortSignal) {
  return Boolean(signal?.aborted);
}

function createAbortError() {
  return new DOMException('Generation stopped.', 'AbortError');
}

async function generateAds(
  formData: AdFormData,
  batchOffset = 0,
  existingGenerationId?: string,
  skipCopyGeneration = false,
  callImageCount?: number,
  options: GenerateAdsOptions = {}
): Promise<unknown> {
  // Use the logged-in user's session access token — NOT the anon key.
  // The edge function will derive the verified userId from this token server-side.
  // userId is intentionally NOT sent in the body to prevent spoofing.

  // Try getSession first, then fall back to getUser to ensure we have a fresh token
  let accessToken: string | null = null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    accessToken = session?.access_token ?? null;

    // If no session from cache, try refreshing
    if (!accessToken) {
      const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
      accessToken = refreshedSession?.access_token ?? null;
    }
  } catch (authErr) {
    console.warn('[generateAds] Could not get session token:', authErr);
    accessToken = null;
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    // Authenticated request: bearer is the user's JWT, not the anon key
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  // For anonymous requests (no session), send no Authorization header —
  // the edge function handles the anonymous path without auth.

  // Strip userId from the body — the edge function derives it from the token
  const { ...bodyWithoutUserId } = formData;

  if (isGenerationAborted(options.signal)) {
    throw createAbortError();
  }

  const response = await fetch(API_URL, {
    method: 'POST',
    headers,
    signal: options.signal,
    body: JSON.stringify({
      ...bodyWithoutUserId,
      batchOffset,
      skipCopyGeneration,
      ...(existingGenerationId ? { generationId: existingGenerationId } : {}),
      requestedTotalImageCount: formData.imageCount ?? 1,
      callImageCount,
      // Override imageCount with the per-call count so the edge function generates
      // exactly the right number of images for this batch window.
      // The edge function calculates: callImageCount = min(MAX_IMAGES_PER_CALL, imageCount - batchOffset)
      // By passing imageCount = batchOffset + callImageCount, we get exactly callImageCount images.
      ...(callImageCount !== undefined ? { imageCount: batchOffset + callImageCount } : {}),
    }),
  });

  if (response.status === 403) {
    const body = await response.json().catch(() => ({})) as Record<string, unknown>;
    if (body?.error === 'INSUFFICIENT_CREDITS') {
      throw new InsufficientCreditsError(typeof body.creditsRemaining === 'number' ? body.creditsRemaining : 0);
    }
    throw new QuotaExceededError();
  }

  if (response.status === 429) {
    const body = await response.json().catch(() => ({})) as Record<string, unknown>;
    if (body?.error === 'PRO_BULK_DAILY_CAP_EXCEEDED') {
      throw new ProBulkCapError(
        typeof body.dailyCap === 'number' ? body.dailyCap : 3,
        typeof body.used === 'number' ? body.used : 3,
      );
    }
    if (body?.error === 'PRO_BULK_COOLDOWN') {
      throw new ProBulkCooldownError(typeof body.remainingMin === 'number' ? body.remainingMin : 10);
    }
    const serverMessage =
      typeof body.message === 'string' ? body.message :
      typeof body.error === 'string' ? body.error :
      'Rate limit exceeded. Please try again shortly.';
    throw new Error(serverMessage);
  }

  if (!response.ok) {
    throw new Error(
      'Request failed: ' + response.status + ' ' + response.statusText
    );
  }

  const data = await response.json();
  return data;
}

function toArray(val: unknown): string[] {
  if (Array.isArray(val))
    return (val as unknown[]).map(String).filter(Boolean);
  if (typeof val === 'string' && val.trim()) return [val];
  return [];
}

function getStringArray(val: unknown): string[] {
  return Array.isArray(val) ? (val as unknown[]).map(String).filter(Boolean) : [];
}

function hasGeneratedImage(data: Record<string, unknown>) {
  return (
    getStringArray(data.imageStorageUrls).length > 0 ||
    getStringArray(data.imageUrls).length > 0 ||
    getStringArray(data.imageBase64s).length > 0 ||
    (typeof data.imageUrl === 'string' && data.imageUrl.trim().length > 0) ||
    (typeof data.imageBase64 === 'string' && data.imageBase64.trim().length > 0)
  );
}

function getBatchFailureMessage(data: Record<string, unknown>) {
  const batchResult = data.batchResult as Record<string, unknown> | undefined;
  const errors = Array.isArray(batchResult?.errors) ? batchResult.errors : [];
  if (errors.length === 0) return 'Image batch returned no images.';
  return errors
    .map((err) => {
      const e = err as { index?: number; status?: number; message?: string };
      return `[img ${typeof e.index === 'number' ? e.index + 1 : '?'}] ${e.message ?? `HTTP ${e.status ?? '?'}`}`;
    })
    .join('; ');
}

function parseApiResponse(data: Record<string, unknown>): AdResult {
  const overlayRaw = data.overlayText as Record<string, unknown> | undefined;

  // Parse multi-image arrays
  const imageUrls = Array.isArray(data.imageUrls)
    ? (data.imageUrls as unknown[]).map(String).filter(Boolean)
    : undefined;
  const imageStorageUrls = Array.isArray(data.imageStorageUrls)
    ? (data.imageStorageUrls as unknown[]).map(String).filter(Boolean)
    : undefined;
  const imageBase64s = Array.isArray(data.imageBase64s)
    ? (data.imageBase64s as unknown[]).map(String).filter(Boolean)
    : undefined;
  const imagePrompts = Array.isArray(data.imagePrompts)
    ? (data.imagePrompts as unknown[]).map(String).filter(Boolean)
    : undefined;
  // Auto-selected style IDs returned by the edge function when stylePack is "auto"
  const autoSelectedStyleIds = Array.isArray(data.autoSelectedStyleIds)
    ? (data.autoSelectedStyleIds as unknown[]).map(String).filter(Boolean)
    : undefined;

  return {
    hooks: toArray(
      data.hooks ?? data.hook ?? data.openingHooks ?? data.opening_hooks
    ),
    headlines: toArray(
      data.headlines ?? data.headline ?? data.titles ?? data.title
    ),
    subheadlines: toArray(
      data.subheadlines ?? data.subheadline ?? data.sub_headlines ?? data.subTitles
    ),
    primaryText: toArray(
      data.primaryText ??
        data.primary_text ??
        data.bodyCopies ??
        data.body_copies ??
        data.adCopy ??
        data.ad_copy ??
        data.copy ??
        data.body ??
        data.mainCopy ??
        data.main_copy
    ),
    ctas: toArray(
      data.ctas ??
        data.cta ??
        data.callToActions ??
        data.call_to_actions ??
        data.buttons
    ),
    // The DB row ID so the frontend can update the record later (e.g. after single-image regen)
    generationId: typeof data.generationId === 'string' && data.generationId ? data.generationId : undefined,
    generationWarning: typeof data.generationWarning === 'string' && data.generationWarning ? data.generationWarning : undefined,
    requestedImageCount: typeof data.requestedImageCount === 'number' ? data.requestedImageCount : undefined,
    generatedImageCount: typeof data.generatedImageCount === 'number' ? data.generatedImageCount : undefined,
    // Multi-image arrays — imageUrls already prefers storage URLs from edge function
    imageUrls: imageUrls && imageUrls.length > 0 ? imageUrls : undefined,
    imageStorageUrls: imageStorageUrls && imageStorageUrls.length > 0 ? imageStorageUrls : undefined,
    imageBase64s: imageBase64s && imageBase64s.length > 0 ? imageBase64s : undefined,
    imagePrompts: imagePrompts && imagePrompts.length > 0 ? imagePrompts : undefined,
    autoSelectedStyleIds: autoSelectedStyleIds && autoSelectedStyleIds.length > 0 ? autoSelectedStyleIds : undefined,
    // Legacy single
    imageUrl:
      typeof data.imageUrl === 'string' && data.imageUrl.trim()
        ? data.imageUrl.trim()
        : undefined,
    imageBase64:
      typeof data.imageBase64 === 'string' && data.imageBase64.trim()
        ? data.imageBase64.trim()
        : undefined,
    imagePrompt:
      typeof data.imagePrompt === 'string' && data.imagePrompt.trim()
        ? data.imagePrompt.trim()
        : undefined,
    overlayText:
      overlayRaw &&
      typeof overlayRaw.headline === 'string' &&
      typeof overlayRaw.subheadline === 'string' &&
      typeof overlayRaw.cta === 'string'
        ? {
            headline: overlayRaw.headline,
            subheadline: overlayRaw.subheadline,
            cta: overlayRaw.cta,
          }
        : undefined,
  };
}

export async function fetchGeneratedAds(
  formData: AdFormData,
  _userIdOrOptions?: string | GenerateAdsOptions,
  maybeOptions?: GenerateAdsOptions
): Promise<AdResult> {
  const options = typeof _userIdOrOptions === 'object' && _userIdOrOptions !== null
    ? _userIdOrOptions
    : maybeOptions ?? {};

  // Per-call image limits by provider:
  // - Stability (draft): each image ~28s sequential → cap at 1 per call to stay well under timeouts
  // - OpenAI/NanoBanana (standard/pro): cap at 2 per call (2×35s = 70s, safe under 150s limit)
  const MAX_PER_CALL = 1;
  const totalCount = formData.imageCount ?? 1;

  // If the total fits in one call, use the simple path
  if (totalCount <= MAX_PER_CALL) {
    const raw = await generateAds(formData, 0, undefined, false, totalCount, options);
    const data = raw as Record<string, unknown>;

    const batchResult = data.batchResult as Record<string, unknown> | undefined;
    if (batchResult && batchResult.completed === 0 && Array.isArray(batchResult.errors) && batchResult.errors.length > 0) {
      const errorSummary = (batchResult.errors as Array<{ index: number; status: number; message: string }>)
        .map((e) => `[img ${e.index}] HTTP ${e.status}: ${e.message}`)
        .join('; ');
      console.error('[generateAds] All images failed. batchResult.errors:', batchResult.errors);
      throw new Error(`Image generation failed (0/${batchResult.batchSize ?? '?'} completed). Errors: ${errorSummary}`);
    }
    if (!hasGeneratedImage(data)) {
      throw new Error(getBatchFailureMessage(data));
    }

    return parseApiResponse(data);
  }

  // Split into multiple calls of MAX_PER_CALL each.
  // First call: generates copy + first batch of images, deducts credits for the FULL totalCount.
  // The edge function calculates and deducts credits server-side.
  const firstCallCount = Math.min(MAX_PER_CALL, totalCount);
  const firstRaw = await generateAds(
    formData,
    0,
    undefined,
    false,
    firstCallCount, // generate only this many images in this call
    options,
  );
  const firstData = firstRaw as Record<string, unknown>;

  // Extract generationId from first call to pass to subsequent calls
  const generationId = typeof firstData.generationId === 'string' ? firstData.generationId : undefined;

  // Collect all image arrays
  const allImageUrls: string[] = getStringArray(firstData.imageUrls);
  const allImageStorageUrls: string[] = getStringArray(firstData.imageStorageUrls);
  const allImageBase64s: string[] = getStringArray(firstData.imageBase64s);
  const allImagePrompts: string[] = getStringArray(firstData.imagePrompts);
  const firstAutoStyleIds: string[] = getStringArray(firstData.autoSelectedStyleIds);
  const rawPreassignedStyleIds = getStringArray(firstData.preassignedStyleIds);
  const firstPreassignedStyleIds: string[] = rawPreassignedStyleIds.length > 0
    ? rawPreassignedStyleIds
    : firstAutoStyleIds;
  const allAutoStyleIds: string[] = firstAutoStyleIds;

  // Subsequent calls: images only, no copy generation, no credit deduction.
  // IMPORTANT: the edge function pre-assigns ALL N style IDs in call 1's autoSelectedStyleIds
  // (even though call 1 only generates 1 image). We pass the full locked list as stylePackIds
  // so each subsequent call gets a genuinely different style — not a fresh random shuffle.
  // If call 1 only returned 1 style ID (old edge function), lockedStyleIds will be undefined
  // and each call falls back to random — which is the bug we're fixing.
  const lockedStyleIds = firstPreassignedStyleIds.length >= totalCount ? firstPreassignedStyleIds : undefined;
  let partialWarning: string | undefined;

  for (let offset = MAX_PER_CALL; offset < totalCount; offset += MAX_PER_CALL) {
    try {
      if (isGenerationAborted(options.signal)) {
        throw createAbortError();
      }
      const thisCallCount = Math.min(MAX_PER_CALL, totalCount - offset);
      const batchFormData = lockedStyleIds
        ? { ...formData, stylePackIds: lockedStyleIds }
        : formData;
      let batchData: Record<string, unknown> | null = null;
      let lastBatchError: unknown = null;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const batchRaw = await generateAds(batchFormData, offset, generationId, true, thisCallCount, options);
          const candidate = batchRaw as Record<string, unknown>;
          if (hasGeneratedImage(candidate)) {
            batchData = candidate;
            break;
          }
          lastBatchError = new Error(getBatchFailureMessage(candidate));
        } catch (err) {
          lastBatchError = err;
        }

        if (isGenerationAborted(options.signal)) {
          throw createAbortError();
        }

        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
        }
      }

      if (!batchData) {
        throw lastBatchError instanceof Error
          ? lastBatchError
          : new Error('Image batch returned no images after retrying.');
      }

      allImageUrls.push(...getStringArray(batchData.imageUrls));
      allImageStorageUrls.push(...getStringArray(batchData.imageStorageUrls));
      allImageBase64s.push(...getStringArray(batchData.imageBase64s));
      allImagePrompts.push(...getStringArray(batchData.imagePrompts));

      const batchStyleIds = getStringArray(batchData.autoSelectedStyleIds);
      if (batchStyleIds.length > 0) {
        allAutoStyleIds.push(...batchStyleIds.slice(0, thisCallCount));
      } else if (lockedStyleIds) {
        allAutoStyleIds.push(...lockedStyleIds.slice(offset, offset + thisCallCount));
      }
    } catch (batchErr) {
      if (isGenerationAborted(options.signal)) {
        throw batchErr;
      }
      // Do not present a partial batch as success. Missing slots should retry or fail clearly.
      console.warn(`[generateAds] Batch call at offset ${offset} failed:`, batchErr);
      partialWarning = batchErr instanceof Error
        ? batchErr.message
        : `Image batch ${offset + 1} failed.`;
    }
  }

  const generatedCount = Math.max(allImageStorageUrls.length, allImageUrls.length, allImageBase64s.length);
  if (generatedCount === 0) {
    throw new Error(partialWarning ?? 'No images were generated. Please try again.');
  }
  if (generatedCount < totalCount) {
    partialWarning = `Generated ${generatedCount}/${totalCount} creatives. ${partialWarning ?? 'One creative could not be generated after retrying.'}`;
  }

  // Merge everything into a single AdResult using the copy from the first call
  const merged: Record<string, unknown> = {
    ...firstData,
    imageUrls: allImageUrls,
    imageStorageUrls: allImageStorageUrls,
    imageBase64s: allImageBase64s,
    imagePrompts: allImagePrompts,
    imageUrl: allImageStorageUrls[0] ?? allImageUrls[0] ?? null,
    imageBase64: allImageBase64s[0] ?? null,
    imagePrompt: allImagePrompts[0] ?? null,
    autoSelectedStyleIds: allAutoStyleIds.length > 0 ? allAutoStyleIds : undefined,
    generationWarning: partialWarning,
    requestedImageCount: totalCount,
    generatedImageCount: generatedCount,
    generationId,
  };

  return parseApiResponse(merged);
}
